import { useCan } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { DiscountCategoryDetail } from '@/services/discount-management/discount-slabs.types';
import { useParams } from '@tanstack/react-router';
import * as React from 'react';
import { ManageDiscountCategorySlabForm } from '../../category/ManageDiscountCategorySlabForm';
import { useManageDiscountCategoryDetailStatus } from '../../hooks/useManageCategoryDetailStatus';
import { getColumns } from './Column';

export function DiscountSlabsTable({
  data,
  isLoading = false,
  pagination,
}: {
  data: DiscountCategoryDetail[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const { openConfirmation } = useConfirmationStore();
  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const { discountCategory } = useParams({
    from: '/(app)/configuration-management/discount-management/$discountCategory/',
  });
  const { mutateAsync } = useManageDiscountCategoryDetailStatus();
  const canAddNew = useCan({ allOf: [PERMS.discount.category.detailManage] });

  const columns = React.useMemo(
    () => getColumns({ t, openConfirmation, onOpen, mutateAsync, categoryId: discountCategory }),
    [t, openConfirmation, onOpen, mutateAsync, discountCategory],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      title: 'Add New Discount Slab',
      okText: <CrumbTitle id="common.page.modal.submit" />,
      size: 'lg',
      modalTitle: 'configurationManagement.discountManagement.slab.modal.title',
      component: <ManageDiscountCategorySlabForm discountCategoryId={discountCategory} />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'discountCategoryDetailId', desc: true }],
    },
    meta: {
      title: t('configurationManagement.discountManagement.slab.table.title'),
      actionKey: canAddNew ? 'onAddNew' : '',
      action: canAddNew ? onAddNew : null,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.discountCategoryDetailId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
